import React from "react";
import { useLang } from "@/context/LangContext";
import { QRCodeSVG } from "qrcode.react";
import { MessageCircle, Copy, Mail } from "lucide-react";
import { toast } from "sonner";

function CardWeChat({ recommended, t }) {
  return (
    <div
      data-testid="consultant-wechat-card"
      className={`relative rounded-2xl border bg-white p-7 flex flex-col items-center text-center lift ${recommended ? 'border-[hsl(var(--primary))] ring-1 ring-[hsl(var(--primary))]/30' : 'border-[hsl(var(--border))]'}`}
    >
      {recommended && (
        <span className="absolute -top-2.5 right-5 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white">
          {t.contact.recommended}
        </span>
      )}
      <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[hsl(var(--muted-foreground))]">
        <MessageCircle className="w-3.5 h-3.5" />
        {t.contact.wechat_label}
      </div>
      <div className="mt-5 rounded-xl border border-[hsl(var(--border))] p-3 bg-[#FDFBF7]">
        <img
          src={`${process.env.PUBLIC_URL || ""}/qr/wechat-twt02648.jpg`}
          alt="WeChat QR code"
          data-testid="consultant-wechat-qr"
          className="w-48 h-48 object-contain"
        />
      </div>
      <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">{t.consultant.scan_wechat}</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs">
        <span className="text-[hsl(var(--muted-foreground))]">ID</span>
        <code className="px-2 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">{t.contact.wechat}</code>
        <button
          type="button"
          aria-label="Copy WeChat ID"
          data-testid="consultant-wechat-copy"
          onClick={() => { navigator.clipboard?.writeText(t.contact.wechat); toast.success(t.consultant.copied); }}
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CardLine({ t }) {
  // Line "Add Friend" deep link by ID. Works on iOS / Android Line app.
  const lineUrl = `https://line.me/ti/p/~${t.contact.line}`;
  return (
    <div data-testid="consultant-line-card" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-7 flex flex-col items-center text-center lift">
      <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[hsl(var(--muted-foreground))]">
        <MessageCircle className="w-3.5 h-3.5" />
        {t.contact.line_label}
      </div>
      <div className="mt-5 rounded-xl border border-[hsl(var(--border))] p-3 bg-white">
        <QRCodeSVG
          value={lineUrl}
          size={192}
          level="M"
          fgColor="#1A1C1A"
          bgColor="#FFFFFF"
          marginSize={2}
          data-testid="consultant-line-qr"
        />
      </div>
      <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">{t.consultant.scan_line}</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs">
        <span className="text-[hsl(var(--muted-foreground))]">ID</span>
        <code className="px-2 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">{t.contact.line}</code>
        <button
          type="button"
          aria-label="Copy Line ID"
          data-testid="consultant-line-copy"
          onClick={() => { navigator.clipboard?.writeText(t.contact.line); toast.success(t.consultant.copied); }}
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <a
        href={lineUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="consultant-line-open"
        className="mt-4 text-xs text-[hsl(var(--primary))] hover:underline"
      >
        {t.consultant.open_in_line} →
      </a>
    </div>
  );
}

export default function ConsultantPanel() {
  const { t } = useLang();
  return (
    <div data-testid="consultant-panel" className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 p-6 md:p-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-4">
          <p className="text-xs tracking-[0.25em] uppercase text-[hsl(var(--primary))]">{t.consultant.eyebrow}</p>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-3">{t.consultant.title}</h3>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">{t.consultant.body}</p>
          <ul className="mt-6 space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
            {t.consultant.points.map((p, i) => (
              <li key={i} className="flex gap-2"><span className="text-[hsl(var(--primary))]">·</span>{p}</li>
            ))}
          </ul>
          <div className="mt-7 rounded-xl border border-[hsl(var(--border))] bg-white p-4 flex items-start gap-3">
            <Mail className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))]" />
            <div className="text-xs">
              <p className="text-[hsl(var(--muted-foreground))] uppercase tracking-[0.2em]">{t.consultant.or_email}</p>
              <a href={`mailto:${t.contact.email}`} className="text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))] break-all" data-testid="consultant-email-link">{t.contact.email}</a>
            </div>
          </div>
        </div>

        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <CardWeChat recommended t={t} />
          <CardLine t={t} />
        </div>
      </div>
    </div>
  );
}
